import React, { useState, useEffect } from 'react';
import { appConfig } from '../AppConfig';
import { getDecryptLocalStorage, setEncryptLocalStorage } from '../Utils/localStorageHelper';

// use this component to open Google Picker for file selection
// refer to Rappit team for more information
interface UseGooglePickerReturn {
    openPicker: () => void;
}

const timeLimit = 3599;
const refreshPreset = 59;

export const useGooglePicker = (onPick: (data: any) => void, viewMimeTypes: string): UseGooglePickerReturn => {
    const [picker, setPicker] = useState<any>(null);

    function initTokenClient(): any {
        return new Promise((resolve) => {
            // Always force consent to ensure fresh token
            const client = window.google.accounts.oauth2.initTokenClient({
                client_id: appConfig.GOOGLE_CLIENT_ID,
                scope: 'https://www.googleapis.com/auth/drive.readonly',
                prompt: 'consent',
                callback: (tokenResponse: any) => {
                    if (tokenResponse?.access_token) {
                        setEncryptLocalStorage('pc_t', tokenResponse.access_token, true);
                        localStorage.setItem('expiresBy', new Date(new Date().getTime() + ((timeLimit - refreshPreset) * 1000)).toISOString());
                        setTimeout(() => {
                            localStorage.removeItem('pc_t');
                            localStorage.removeItem('expiresBy');
                        }, ((timeLimit - refreshPreset) * 1000));
                        
                        localStorage.setItem('hasConsented', 'true');
                        setPicker(null); // Reset picker to ensure fresh creation
                        
                        // Add a small delay to ensure token is properly stored
                        setTimeout(() => {
                            openPicker();
                        }, 100);
                    } else {
                        console.error('Failed to receive OAuth token.');
                        const hasConsented = localStorage.getItem('hasConsented') === 'true';
                        if (hasConsented) {
                            localStorage.removeItem('hasConsented');
                            // need timeout, immediate calling of picker after an error 
                            // blocks the browser pop-up due to repetitive usage of pop-up by the same script
                            setTimeout(() => {
                                openPicker();
                            }, 2750);
                        }
                    }
                },
            });
            resolve(client);
        });
    }

    // Load Google API Scripts
    useEffect(() => {
        const loadGisScript = (): Promise<void> => {
            return new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client';
                script.onload = () => resolve();
                document.body.appendChild(script);
            });
        };

        const loadPickerScript = (): Promise<void> => {
            return new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://apis.google.com/js/api.js';
                script.onload = () => {
                    window.gapi.load('picker', {
                        callback: resolve,
                    });
                };
                document.body.appendChild(script);
            });
        };

        loadGisScript()
            .then(loadPickerScript)
            .catch((error) => console.error('Error loading scripts', error));
    }, []);

    const waitForPickerLoad = (callback: () => void) => {
        const checkPickerLoaded = () => {
            if (window.google?.picker) {
                callback();
            } else {
                setTimeout(checkPickerLoaded, 500);
            }
        };
        checkPickerLoaded();
    };

    // Create the Picker UI
    const openPicker = () => {
        console.log('openPicker called');
        const oauthToken = (getDecryptLocalStorage('pc_t', true));
        const expiresBy = localStorage.getItem('expiresBy');
        
        console.log('Token:', oauthToken ? 'exists' : 'missing');
        console.log('Expires by:', expiresBy);
        
        // Check if token exists and is not expired
        const isTokenValid = oauthToken && expiresBy && new Date(expiresBy) > new Date();
        
        console.log('Token valid:', isTokenValid);
        
        if (isTokenValid) {
            if (!picker) {
                waitForPickerLoad(() => {
                    // Create a Recent view for recently accessed files
                    const recentView = new google.picker.DocsView(window.google.picker.ViewId.RECENTLY_PICKED);
                    recentView.setSelectFolderEnabled(false); // Restrict picking folders
                    recentView.setIncludeFolders(true); // Include folders
                    recentView.setOwnedByMe(true); // Limit to files owned by the user
                    recentView.setMode(google.picker.DocsViewMode.LIST); // Set to list view mode
                    recentView.setMimeTypes(viewMimeTypes); // Limit files based on MimeTypes
                    
                    const myDriveView = new google.picker.DocsView(window.google.picker.ViewId.DOCS);
                    myDriveView.setSelectFolderEnabled(false); // Restrict picking folders
                    myDriveView.setIncludeFolders(true); // Include folders
                    myDriveView.setOwnedByMe(true); // Limit to files owned by the user
                    myDriveView.setMode(google.picker.DocsViewMode.LIST); // Set to list view mode
                    myDriveView.setMimeTypes(viewMimeTypes); // Limit files based on MimeTypes

                    // Shared Drives view
                    const sharedDriveView = new google.picker.DocsView(window.google.picker.ViewId.DOCS);
                    sharedDriveView.setSelectFolderEnabled(false); // Restrict picking folders
                    sharedDriveView.setIncludeFolders(true); // Include folders
                    sharedDriveView.setOwnedByMe(false); // Limit to shared files
                    sharedDriveView.setMode(google.picker.DocsViewMode.LIST); // Set to list view mode
                    sharedDriveView.setMimeTypes(viewMimeTypes); // Limit files based on MimeTypes
                    
                    // Dedicated Shared Drives (Team Drives) view
                    const teamDrivesView = new google.picker.DocsView(window.google.picker.ViewId.SHARED_DRIVES);
                    teamDrivesView.setSelectFolderEnabled(false);
                    teamDrivesView.setIncludeFolders(true);
                    teamDrivesView.setMode(google.picker.DocsViewMode.LIST);
                    teamDrivesView.setMimeTypes(viewMimeTypes);
                    (teamDrivesView as any).setLabel("Shared Drives");
                    // Build the Picker with multiple views
                    const pickerInstance = new window.google.picker.PickerBuilder()
                        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED) 
                        .addView(recentView) // Add Recent tab first
                        .addView(myDriveView) // My Drive view
                        .addView(sharedDriveView) // Shared files view
                        .addView(teamDrivesView) // Shared Drives/Team Drives view
                        .setOAuthToken(oauthToken)
                        .setDeveloperKey(appConfig.GOOGLE_API_KEY)
                        .setCallback((data: any) => {
                            // Detect 403 error from Picker
                            if (data?.error === 'access_denied' || data?.error === '403') {
                                console.error('Google Picker access denied or 403 error', data);
                                // Clear tokens and consent, force re-auth
                                localStorage.removeItem('pc_t');
                                localStorage.removeItem('expiresBy');
                                localStorage.removeItem('hasConsented');
                                setPicker(null); // Reset picker
                                // Optionally, show a message to user here
                                setTimeout(() => {
                                    handleAuthClick();
                                }, 500);
                                return;
                            }
                            onPick(data);
                        })
                        .build();
                    
                    setPicker(pickerInstance);
                    pickerInstance?.setVisible(true);
                });
            } else {
                picker?.setVisible(true);
            }
        } else {
            // Token expired or missing - clear everything and re-authenticate
            localStorage.removeItem('pc_t');
            localStorage.removeItem('expiresBy');
            localStorage.removeItem('hasConsented');
            setPicker(null);
            handleAuthClick();
        }
    };

    // Handle the OAuth token request
    async function handleAuthClick() {
        const client = await initTokenClient();
        client.requestAccessToken();
    };

    return { openPicker };
};

export default useGooglePicker;